/**
 * Food entity — a static, immobile boid that humans seek and eat. Lives on
 * the `'food'` layer (`World.layerByName('food')`), grows over time up to
 * half a flow-cell, and toggles its contribution to the food-gradient flow
 * field on/off as it crosses `Food.MinSize` and the regrowth threshold. Both
 * transitions call `World.markFoodGradientDirty()` so the gradient is
 * recomputed once next frame rather than per-state-change.
 *
 * @see Boid for the base class and the `id`-as-GL-slot invariant.
 */
import { IGameTime } from '../GameClock';
import { Boid, IBoidOptions } from './Boid';

export class Food extends Boid {
  static MinSize: number = 2;
  flowEnabled: boolean = true;

  constructor(options: IBoidOptions) {
    super(options);

    this.layer = this.options.world.layerByName('food');
    this.color.rgb = [1, 1, 0]; // food is yellow
    this.maxSpeed = 0;
    this.r = this.grid.cellSize * 0.5;
    this.static = true;
    this.World.food.add(this);
  }

  override tick(gameTime: IGameTime): void {
    super.tick(gameTime);
    // ARC-010: previously `if (!super.tick(gameTime)) return false;` consumed
    // Boid.tick's boolean return. Now that IProgressible.tick is void, check
    // this.alive directly — a dead Food skips growth/gradient work.
    if (!this.alive) {
      return;
    }

    this.r += gameTime.deltaTime * 0.5;
    this.r = Math.min(this.grid.cellSize * 0.5, this.r);
    if (this.flowEnabled && this.r < Food.MinSize) {
      this.flowEnabled = false;
      this.World.markFoodGradientDirty();
    } else if (!this.flowEnabled && this.r >= this.grid.cellSize * 0.45) {
      this.flowEnabled = true;
      this.World.markFoodGradientDirty();
    }
  }

  override die() {
    super.die();
    this.World.food.delete(this);
    this.flowEnabled = false;
    this.World.markFoodGradientDirty();
  }
}
